import { TimelineEvent, TimelineEventType } from '../types/timeline';

/**
 * Deterministic identity for a synthesized timeline event.
 *
 * Report drafts persist these IDs (membership, overrides, comments), so they must
 * be stable across renders and reloads — never derived from array position.
 *
 * Order matters: a completed work order that belongs to a project carries both
 * workOrderKey and projectId; the work order key wins so the event keeps one ID.
 *
 * COMPONENT_REPLACED uses the archived component's key (unique per replacement,
 * survives date edits). A plan activation uses the building and the first
 * activation of its re-activation run, so the ID survives later re-activations
 * joining the run. Inspection and legacy component IDs embed the event date,
 * so editing a source's date shows up in a draft sync as one removed + one added
 * event — accepted behaviour (plan D3).
 */
export function getEventId(event: TimelineEvent): string {
    if (event.customEventId) return `custom-${event.customEventId}`;
    if (event.workOrderKey) return `wo-${event.workOrderKey}`;
    if (event.projectId) return `project-${event.projectId}`;
    if (event.eventType === TimelineEventType.COMPONENT_REPLACED && event.replacedComponentKey) {
        return `comp-${event.replacedComponentKey}`;
    }
    if (event.maintenancePlan) return `plan-${event.buildingKey}-${event.maintenancePlan.firstActivatedAt}`;
    if (event.componentKey) return `comp-${event.componentKey}-${event.date.getTime()}`;
    if (event.inspectionTemplateId) return `insp-${event.buildingKey}-${event.inspectionTemplateId}-${event.date.getTime()}`;
    // Defensive only: every builder in the timeline synthesis sets one of the
    // discriminators above (verified against TimelinePage builders, plan D3/F4).
    console.warn('Timeline event without discriminator fields, using fallback ID:', event.eventType, event.buildingKey);
    return `misc-${event.buildingKey}-${event.eventType}-${event.date.getTime()}`;
}
